import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import * as _ from 'lodash';
import Countdown from 'react-countdown';
import { authActions } from '../../../redux/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import { SquareInputBox } from '../../SquareInputBox/SquareInputBox';
import { OtpVerifyStatus } from '../../../utils/enums/otp-verify-status.enum';
import BillingDetails from '../BillingDetails/BillingDetails';
import LogoSVG from '../../../assets/img/sidebar/logo.svg';
import WarningRedSvgw from '../../../assets/img/warning-red.svg';
import CheckmarkCircleSvg from '../../../assets/img/checkmark-circle.svg';
import LogoLink from '../../LogoLink/LogoLink';

function Otp({ email }: { email: string }) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [verificationCode, setVerificationCode] = useState<string[]>(Array(4).fill(''));
  const inputRefs = useRef<HTMLInputElement[]>(Array(4).fill(null));
  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const verifyStatus = _.get(componentStatus, 'verifyStatus', undefined) as OtpVerifyStatus;
  const error = _.get(componentStatus, 'error', '') as string;
  const countdownStartTime = Number(_.get(componentStatus, 'countdownStartTime', Date.now().toString()));
  const isSubmitting = _.get(componentStatus, 'isSubmitting', false) as boolean;
  const [isVerified, setIsVerified] = useState<boolean>(false);

  useEffect(() => {
    if (verifyStatus === OtpVerifyStatus.VERIFIED) {
      const timer = setTimeout(() => {
        setIsVerified(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [verifyStatus]);

  const handleVerificationCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    if (!Number.isNaN(Number(event.target.value)) && index < 4) {
      const code = [...verificationCode];
      code[index] = event.target.value.slice(-1);
      setVerificationCode(code);

      if (event.target.value !== '' && index < 3) {
        inputRefs.current[index + 1].focus();
      }
      if (_.compact(code).length === 4 && index === 3) {
        dispatch(authActions.validateOtpAsync({ code: code.join(''), email }));
      }
    }
  };
  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleResendEmailOtp = () => {
    if (!isSubmitting) {
      setVerificationCode(verificationCode.fill(''));
      dispatch(authActions.setComponentStatus({ error: '', verifyStatus: undefined }));
      dispatch(authActions.resendEmailOtpAsync({ email }));
    }
  };

  const keyMap: number[] = [0, 1, 2, 3];

  if (isVerified) {
    return <BillingDetails />;
  }

  return (
    <Grid
      display="flex"
      gap={6}
      flexDirection="column"
      sx={{
        maxWidth: '416px',
        backgroundColor: '#FFFFFF',
        padding: '2%',
        borderRadius: '2%',
        border: '1px solid #DCE0E5',
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
        <LogoLink padding="56px" width="173.343px" height="32px" />

        <Typography
          variant="calloutInterRegular"
          component="p"
          textAlign="center"
          color="#525E6F"
          padding="24px 40px 24px 20px"
        >
          {/* eslint-disable-next-line prettier/prettier, max-len */}
          We safeguard your registration and protect your Personal information. Please enter the 4-digit code sent to{' '}
          {email}
        </Typography>
        <Grid gap={1.5} display="flex" flexDirection="column">
          <Box display="flex" columnGap={3} justifyContent="center">
            {verificationCode.map((e, index) => (
              <SquareInputBox
                theme={theme}
                height="40px"
                width="40px"
                autoComplete="off"
                disabled={
                  verificationCode.filter((code) => code).length === 4 && verifyStatus === OtpVerifyStatus.VERIFYING
                }
                verifystatus={verifyStatus}
                inputRef={(el) => (inputRefs.current[index] = el as HTMLInputElement)}
                autoFocus={index === 0}
                onKeyDown={(key) => handleKeyDown(index, key)}
                color={(() => {
                  if (verifyStatus === undefined) {
                    return 'primary';
                  }
                  if (verifyStatus === OtpVerifyStatus.VERIFYING) {
                    return 'success';
                  }
                  return 'error';
                })()}
                key={keyMap[index]}
                type="text"
                name={`n${index}`}
                value={e}
                onChange={(event) => {
                  handleVerificationCodeChange(event, index);
                }}
                variant="outlined"
              />
            ))}
          </Box>
          {isSubmitting && <LinearProgress color="secondary" />}
          {error && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              alignContent="center"
              gap="8px"
              padding="6px"
            >
              <Box component="img" src={WarningRedSvgw} height="15px" width="14.25px" />
              <Typography variant="footnoteRegular2" component="p" textAlign="center" color="#DF0C3D">
                {error}
              </Typography>
            </Box>
          )}

          {verifyStatus === OtpVerifyStatus.VERIFIED && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              alignContent="center"
              gap="8px"
              padding="6px"
            >
              <Box component="img" src={CheckmarkCircleSvg} height="15px" width="14.25px" />
              <Typography variant="footnoteRegular2" component="p" textAlign="center" color="#00A81C">
                Valid PIN!
              </Typography>
            </Box>
          )}
        </Grid>
        {verifyStatus !== OtpVerifyStatus.VERIFYING && (
          <Box display="flex" justifyContent="center" alignContent="center" paddingTop="20px" paddingBottom="56px">
            <Countdown
              key={countdownStartTime}
              zeroPadTime={2}
              date={countdownStartTime + 60 * 1000}
              renderer={({ minutes, seconds, completed }) => {
                if (completed) {
                  return (
                    <Typography
                      variant="calloutInterRegular"
                      color="#3348FF"
                      sx={{ cursor: 'pointer' }}
                      onClick={handleResendEmailOtp}
                    >
                      Didn’t get the code? Resend
                    </Typography>
                  );
                }
                return (
                  <>
                    <Typography
                      variant="calloutInterRegular"
                      component="p"
                      textAlign="center"
                      color="black.contrastText"
                    >
                      Didn’t get the code? Wait&nbsp;
                    </Typography>
                    <Typography
                      variant="subheadRegular"
                      component="p"
                      textAlign="start"
                      color="black.contrastText"
                      width="45px"
                    >
                      {_.padStart(minutes.toString(), 2, '0')}:{_.padStart(seconds.toString(), 2, '0')}
                    </Typography>
                  </>
                );
              }}
            />
          </Box>
        )}
      </Box>
    </Grid>
  );
}

export default Otp;
